if (!customElements.get("product-configurator")) {
  class ProductConfigurator extends HTMLElement {
    constructor() {
      super();
      this.content = this.querySelector("[data-content]");
      this.loader = this.querySelector(".loader");
      this.footer = this.querySelector("[data-footer]");
      this.footerPrice = this.querySelector("[data-footer-price]");
      this.footerParams = this.querySelector("[data-footer-params]");
      this.footerSubmit = this.querySelector("[data-footer-submit]");
      this.productForm = null;
      this.currencySymbol = this.dataset.currencySymbol || "zł";
      this.variants = JSON.parse(this.dataset.variants || "[]");
      this.isNavigating = false;

      // Generic surcharge product state — keyed by option handle
      this._surchargeData = {};
    }

    connectedCallback() {
      this.productForm = document.querySelector("product-form");
      
      // When the component is detached and re-attached (e.g. during section replacement),
      // we only need to update the product-form reference. We shouldn't re-bind all
      // window/document event listeners as that leads to duplicate handlers and 429 rate limit errors.
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      if (!this.productForm) return;

      // Listen for the drawer opening
      const drawer = this.closest("drawer-section");
      if (drawer) {
        window.addEventListener("drawerOpen", (e) => {
          if (e.detail.id === drawer.id) {
            this.onDrawerOpen();
          }
        });
      }

      // Bind option click handlers (server-rendered labels)
      this.content
        .querySelectorAll(".product-configurator__option")
        .forEach((label) => {
          label.addEventListener("click", (e) => {
            e.preventDefault();
            this.onOptionClick(label);
          });
        });

      this.content.addEventListener(
        "click",
        (e) => {
          const link = e.target.closest(".product-configurator__option-link");
          if (!link) return;

          e.stopPropagation();
        }, true,
      );

      // Wire up add-to-cart button
      if (this.footerSubmit) {
        this.footerSubmit.addEventListener("click", () => {
          this.addToCart();
        });
      }

      // Listen for variant changes from the form (e.g. swatch change) to update the header summary
      window.addEventListener("variantChange", () => {
        this.updatePrices();
      });

      // Handle browser back/forward after pushState navigations
      // Full reload is the most reliable way to restore correct product state
      this._popstateHandler = () => {
        if (window.__drawerHandledPopstate) return;
        window.location.reload();
      };
      window.addEventListener("popstate", this._popstateHandler);
    }

    onDrawerOpen() {
      this.syncActiveStates();
      this.syncSurchargeProductStates();
      this.filterMattresses();
      this.updatePrices();
      this.updateFooter();
      this.loader.style.display = "none";

      // Save reference to original (base) swatch step HTML
      if (!this._baseSwatchHTML) {
        const swatchStep = this.content.querySelector('.product-configurator__step--swatches');
        if (swatchStep) {
          this._baseSwatchHTML = swatchStep.outerHTML;
        }
      }
    }

    /**
     * Handle clicking an option label in the configurator.
     */
    onOptionClick(label) {
      const input = label.querySelector('input[type="radio"]');
      if (input?.disabled) {
        return;
      }

      const step = label.closest(".product-configurator__step");
      const optionIndex = parseInt(step.dataset.optionIndex, 10);
      const optionHandle = step.dataset.optionHandle;
      const value = label.dataset.value;

      if (label.dataset.swatchSurcharge === "true") {
        this.setActiveOption(step, label);
        this.onSurchargeProductChange(`swatch-${optionHandle}`, label);
        this.updateFooter();
        return;
      }

      if (step.classList.contains("product-configurator__step--swatches")) {
        this._surchargeData[`swatch-${optionHandle}`] = {
          active: false,
          selectedVariantId: null,
          title: '',
          price: 0,
          priceFormatted: '',
          propertyName: '',
        };
      }

      // Handle additional options that require navigating to a different product
      if (label.dataset.url) {
        if (this.productForm && this.productForm.selected_values) {
          localStorage.setItem(
            "pdp_selected_values",
            JSON.stringify(this.productForm.selected_values),
          );
        }

        // Show loader inside the drawer
        if (this.loader) this.loader.style.display = "block";

        this.isNavigating = true;

        // Push new URL into history BEFORE fetching
        window.history.pushState(
          { configuratorNav: true, url: label.dataset.url },
          "",
          label.dataset.url,
        );

        this.reloadProductSection(label.dataset.url);
        return;
      }

      // Find the corresponding hidden product-option in the product form
      const productOption = this.productForm.querySelector(
        `product-option[data-handle="${optionHandle}"]`,
      );

      if (productOption) {
        // Check the matching radio in the product form
        const radio = productOption.querySelector(
          `input[type="radio"][value="${CSS.escape(value)}"]`,
        );
        if (radio) {
          radio.checked = true;
        } else {
          // If no radio, we might be dealing with an additional option
          const hiddenInput = productOption.parentElement?.querySelector(
            'input[type="hidden"][name^="properties["]',
          );
          if (hiddenInput) {
            hiddenInput.value = value;
          }
        }

        // Visually update the label and button values in the product form
        const labelText = productOption.querySelector(".product-option__label");
        if (labelText) {
          labelText.innerText = value;
        }
        const dropdownBtnText = productOption.querySelector(
          ".product-option__dropdown-btn > span:first-child",
        );
        if (dropdownBtnText) {
          dropdownBtnText.innerText = value;
        }

        // Update data-value and dispatch valueChange
        productOption.setAttribute("data-value", value);
        productOption.dispatchEvent(new CustomEvent("valueChange"));
      }

      // Update visual active state in this step
      this.setActiveOption(step, label);

      // Re-filter mattresses when sleeping surface size changes
      if (optionHandle === this.dataset.bedSizeHandle) {
        this.filterMattresses();
      }

      // Handle generic surcharge-product steps (headboard, etc.)
      if (this.isSurchargeProductStep(optionHandle)) {
        this.onSurchargeProductChange(optionHandle, label);
        this.updateFooter();
        return;
      }

      // Handle fabric family selection — reload swatches from surcharge product
      if (optionHandle === 'fabric-family') {
        this.onFabricFamilyChange(label);
        // Fabric family doesn't change the base variant — only update the footer
        this.updateFooter();
        return;
      }

      // Update prices and footer after variant change
      setTimeout(() => {
        this.updatePrices();
        this.updateFooter();
      }, 100);
    }

    /**
     * Update the visual active state: toggle classes and checkbox SVGs.
     */
    setActiveOption(step, activeLabel) {
      step.querySelectorAll(".product-configurator__option").forEach((item) => {
        item.classList.remove("product-configurator__option--active");

        // Uncheck the radio inside
        const radio = item.querySelector('input[type="radio"]');
        if (radio) radio.checked = false;
      });

      activeLabel.classList.add("product-configurator__option--active");

      // Check the radio inside
      const radio = activeLabel.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;
    }

    /**
     * Sync the configurator UI to match the current product form state.
     * This reads which radio is checked in each hidden product-option
     * and activates the corresponding label in the configurator.
     */
    syncActiveStates() {
      this.content
        .querySelectorAll(".product-configurator__step")
        .forEach((step) => {
          const optionHandle = step.dataset.optionHandle;
          const productOption = this.productForm?.querySelector(
            `product-option[data-handle="${optionHandle}"]`,
          );

          if (!productOption) return;

          const checkedRadio = productOption.querySelector(
            'input[type="radio"]:checked',
          );
          const currentValue = checkedRadio
            ? checkedRadio.value
            : productOption.dataset.value || "";

          if (currentValue) {
            const matchingLabel = step.querySelector(
              `.product-configurator__option[data-value="${CSS.escape(currentValue)}"]`,
            );
            if (matchingLabel) {
              this.setActiveOption(step, matchingLabel);
            }
          }
        });
    }

    /**
     * Update prices on each option by fetching the main-product section natively.
     */
    updatePrices() {
      // Debounce: if called multiple times within 150ms, only the last call fires
      clearTimeout(this._updatePricesTimer);
      this._updatePricesTimer = setTimeout(() => this._updatePricesNow(), 150);
    }

    _updatePricesNow() {
      if (!this.productForm || !this.content || this.isNavigating) return;

      const variantSelect = this.productForm.querySelector('select[name="id"]');
      if (!variantSelect) return;

      const currentVariantId = variantSelect.value;

      const section =
        this.closest(".shopify-section") ||
        document.querySelector(".section--pdp")?.closest(".shopify-section");
      const sectionId = section
        ? section.id.replace("shopify-section-", "")
        : "";
      const url = sectionId
        ? `${window.location.pathname}?variant=${currentVariantId}&section_id=${sectionId}`
        : `${window.location.pathname}?variant=${currentVariantId}`;

      // Ensure previous fetch token is cleared
      const token = Symbol();
      this.currentFetchToken = token;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.text();
        })
        .then((html) => {
          if (this.currentFetchToken !== token) return;

          const doc = new DOMParser().parseFromString(html, "text/html");

          // Update price placeholders with fresh server-rendered ones
          const fetchedPrices = Array.from(
            doc.querySelectorAll(
              "#drawer-product-configurator [data-option-price]",
            ),
          );
          const currentPrices = Array.from(
            this.content.querySelectorAll("[data-option-price]"),
          );
          const currentPricesByKey = new Map(
            currentPrices.map((el, index) => [
              el.dataset.optionPriceKey || `__index_${index}`,
              el,
            ]),
          );

          fetchedPrices.forEach((newEl, index) => {
            const key = newEl.dataset.optionPriceKey || `__index_${index}`;
            const currentEl = currentPricesByKey.get(key);

            if (currentEl) {
              currentEl.innerHTML = newEl.innerHTML;
            }
          });

          // Update footer price naturally
          const fetchedFooterPrice = doc.querySelector("[data-footer-price]");
          if (this.footerPrice) {
            if (fetchedFooterPrice) {
              this.footerPrice.innerHTML = fetchedFooterPrice.innerHTML;
            }
            this.footerPrice.classList.remove("loading");
          }

          // Update selected options summary
          const fetchedSummary = doc.querySelector(".product__selected-options");
          const currentSummary = document.querySelector(
            ".product__selected-options",
          );
          if (fetchedSummary && currentSummary) {
            currentSummary.innerHTML = fetchedSummary.innerHTML;
            this.bindFabricDetailsButtons(currentSummary);
          }

          // Recalculate footer to include mattress surcharge
          this.updateFooter();
        })
        .catch((err) => {
          if (this.currentFetchToken !== token) return;
          if (this.footerPrice) this.footerPrice.classList.remove("loading");
          console.error(
            "Error fetching configurator prices via Section Rendering API",
            err,
          );
        });
    }

    getActiveMattressCard() {
      if (!this.content) return null;

      return Array.from(
        this.content.querySelectorAll(
          '.product-configurator__step--mattress .product-configurator__option--mattress-card',
        ),
      ).find((card) => {
        const radio = card.querySelector('input[type="radio"]:checked');
        return radio && card.style.display !== "none";
      }) || null;
    }

    getSelectedMattress() {
      const activeCard = this.getActiveMattressCard();
      if (!activeCard) return null;

      const radio = activeCard.querySelector('input[type="radio"]');
      if (!radio || !radio.value) return null;

      const priceEl = activeCard.querySelector("[data-mattress-price]");
      const title = activeCard.dataset.mattressTitle || "";

      let price = 0;
      try {
        const variants = JSON.parse(activeCard.dataset.mattressVariants || "[]");
        const match = variants.find((v) => String(v.id) === String(radio.value));
        if (match) price = match.price;
      } catch (e) {
        console.error(
          `Error parsing price from variants JSON for mattress: ${e.message}`,
          e,
        );
      }

      return {
        variantId: radio.value,
        title,
        price,
        priceFormatted: priceEl ? priceEl.textContent.trim() : "",
      };
    }

    /**
     * Get the currently selected fabric family card (if any premium family is checked).
     */
    getActiveFabricFamilyCard() {
      if (!this.content) return null;

      return Array.from(
        this.content.querySelectorAll(
          '.product-configurator__step--fabric-family .product-configurator__option--fabric-family-card',
        ),
      ).find((card) => {
        const radio = card.querySelector('input[type="radio"]:checked');
        return !!radio;
      }) || null;
    }

    /**
     * Get the selected fabric family surcharge info.
     * Returns null if "Base fabric" is selected (no surcharge).
     * The variant for the cart comes from the swatch step's currently checked radio
     * (which shows the surcharge product's color variants after family change).
     */
    getSelectedFabricFamily() {
      const activeCard = this.getActiveFabricFamilyCard();
      if (!activeCard) return null;

      const title = activeCard.dataset.fabricFamilyTitle || "";
      const price = parseInt(activeCard.dataset.fabricFamilyPrice || "0", 10);
      const priceEl = activeCard.querySelector("[data-fabric-family-price-display]");

      // The variant ID comes from the selected color in the swatch step
      // which now shows the surcharge product's variants
      const variantId = this._fabricFamilySelectedVariantId || "";

      if (!variantId) return null;

      return {
        variantId,
        title,
        price,
        priceFormatted: priceEl ? priceEl.textContent.trim() : "",
      };
    }

    /**
     * Handle fabric family selection.
     * When a premium family is selected, fetch the surcharge product's page
     * and replace the swatch step with its color swatches.
     * When "base fabric" is selected, restore the original swatch step.
     */
    onFabricFamilyChange(label) {
      const radio = label.querySelector('input[type="radio"]');
      const radioValue = radio ? radio.value : "";

      const swatchStep = this.content.querySelector('.product-configurator__step--swatches');
      if (!swatchStep) return;

      // If "Base fabric" selected (empty value), restore original swatches
      if (!radioValue) {
        this._fabricFamilyActive = false;
        this._fabricFamilySelectedVariantId = null;

        if (this._baseSwatchHTML) {
          const temp = document.createElement('div');
          temp.innerHTML = this._baseSwatchHTML;
          const newSwatchStep = temp.firstElementChild;
          swatchStep.replaceWith(newSwatchStep);

          // Re-bind click handlers on the restored swatch options
          newSwatchStep.querySelectorAll('.product-configurator__option').forEach((l) => {
            l.addEventListener('click', (e) => {
              e.preventDefault();
              this.onOptionClick(l);
            });
          });

          // Re-sync active states
          this.syncActiveStates();
        }
        return;
      }

      // Premium family selected — find the surcharge product URL
      const card = label.closest('.product-configurator__option--fabric-family-card');
      if (!card) return;

      const productUrl = card.dataset.fabricFamilyProductUrl;
      if (!productUrl) return;

      this._fabricFamilyActive = true;
      this._fabricFamilySelectedVariantId = null;

      // Fetch the surcharge product data via Shopify .js endpoint
      fetch(productUrl + '.js')
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then((productData) => {
          // .js endpoint returns the product object directly (no wrapper)
          if (productData && productData.variants) {
            this._fabricFamilyVariants = productData.variants.map((v) => ({
              id: v.id,
              title: v.title,
              price: v.price,
              options: [v.option1, v.option2, v.option3].filter(Boolean),
              image: v.featured_image ? v.featured_image.src : null,
            }));
          } else {
            this._fabricFamilyVariants = [];
          }

          // The swatch step uses .product-configurator__swatch-group > .product-configurator__swatch-grid
          // Replace all swatch groups with a new one built from the surcharge product variants.
          const existingGroups = swatchStep.querySelectorAll('.product-configurator__swatch-group');
          if (existingGroups.length > 0) {
            // Extract the checkbox icon SVG from the first existing swatch before clearing
            let checkboxSvg = '';
            const existingCheckbox = swatchStep.querySelector('.product-configurator__swatch-checkbox');
            if (existingCheckbox) {
              checkboxSvg = existingCheckbox.innerHTML;
            }

            // Get the fabric family title for the group header
            const familyTitle = card.dataset.fabricFamilyTitle || '';

            // Remove all existing swatch groups
            existingGroups.forEach((g) => g.remove());

            // Build new swatch group
            const group = document.createElement('div');
            group.className = 'product-configurator__swatch-group';
            group.innerHTML = `
              <h3 class="product-configurator__swatch-group-title">
                ${familyTitle}
              </h3>
              <div class="product-configurator__swatch-grid"></div>
            `;
            const grid = group.querySelector('.product-configurator__swatch-grid');

            this._fabricFamilyVariants.forEach((variant) => {
              const value = variant.options[0] || variant.title;
              const imageUrl = variant.image || '';

              const swatch = document.createElement('label');
              swatch.className = 'product-configurator__option product-configurator__option--swatch';
              swatch.dataset.value = value;
              swatch.innerHTML = `
                <input type="radio" name="configurator-surcharge-color" value="${value}">
                <div class="product-configurator__swatch-card">
                  <div class="product-configurator__swatch-image-wrapper">
                    ${imageUrl ? `<img src="${imageUrl}" alt="${value}" width="108" height="142" loading="lazy">` : ''}
                    <div class="product-configurator__swatch-checkbox">
                      ${checkboxSvg}
                    </div>
                  </div>
                  <div class="product-configurator__swatch-info">
                    <span class="product-configurator__option-value">${value}</span>
                  </div>
                </div>
              `;

              swatch.addEventListener('click', (e) => {
                e.preventDefault();
                this.onFabricFamilySwatchClick(swatchStep, swatch);
              });

              grid.appendChild(swatch);
            });

            swatchStep.appendChild(group);

            // Auto-select the first swatch
            const firstSwatch = grid.querySelector('.product-configurator__option');
            if (firstSwatch) {
              this.onFabricFamilySwatchClick(swatchStep, firstSwatch);
            }
          }
        })
        .catch((err) => {
          console.error('Error loading fabric family swatches:', err);
        });
    }

    /**
     * Handle clicking a swatch in the surcharge product's color step.
     * Does NOT update the base product — only tracks the selected variant
     * for add-to-cart and visually toggles active state.
     */
    onFabricFamilySwatchClick(swatchStep, label) {
      // Visual toggle
      swatchStep.querySelectorAll('.product-configurator__option').forEach((item) => {
        item.classList.remove('product-configurator__option--active');
        const r = item.querySelector('input[type="radio"]');
        if (r) r.checked = false;
      });
      label.classList.add('product-configurator__option--active');
      const radio = label.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      // Track the selected value — we need to find the variant ID
      // from the surcharge product. The swatch uses the option value (e.g. "Haze 03")
      // but we need the variant ID for the cart.
      const value = label.dataset.value || (radio ? radio.value : "");

      // The swatch step may contain a product-form <select> with variant IDs,
      // or we can find the variant from the fetched product data.
      // For simplicity, store the value and resolve the variant ID from the
      // hidden select in the product-form on the surcharge product page.
      // But since we replaced the swatch step with fetched HTML, we need
      // another approach: store variant data from the fetch.

      // Use the variant data we stored during fetch
      if (this._fabricFamilyVariants && value) {
        const matchedVariant = this._fabricFamilyVariants.find((v) =>
          v.options.some((opt) => opt === value)
        );
        if (matchedVariant) {
          this._fabricFamilySelectedVariantId = String(matchedVariant.id);
        }
      }

      this.updateFooter();
    }

    // ──────────────────────────────────────────────────────────
    //  Generic surcharge-product methods
    // ──────────────────────────────────────────────────────────

    /**
     * Check if a step is a generic surcharge-product step.
     */
    isSurchargeProductStep(optionHandle) {
      const step = this.content.querySelector(
        `.product-configurator__step--surcharge-product[data-option-handle="${CSS.escape(optionHandle)}"]`
      );
      return !!step;
    }

    syncSurchargeProductStates() {
      this.content
        .querySelectorAll(".product-configurator__step--surcharge-product")
        .forEach((step) => {
          const optionHandle = step.dataset.optionHandle;
          if (!optionHandle) return;

          let checkedRadio = step.querySelector('input[type="radio"]:checked');
          if (!checkedRadio) {
            checkedRadio = step.querySelector('input[type="radio"]');
            if (checkedRadio) checkedRadio.checked = true;
          }

          const activeOption = checkedRadio?.closest(".product-configurator__option");
          if (!activeOption) return;

          this.setActiveOption(step, activeOption);
          this.onSurchargeProductChange(optionHandle, activeOption);
        });
    }

    /**
     * Handle selecting a surcharge product card.
     * Tracks the selected variant for cart and price display.
     * Does NOT touch the swatch step.
     */
    onSurchargeProductChange(optionHandle, label) {
      const radio = label.querySelector('input[type="radio"]');
      const radioValue = radio ? radio.value : '';

      // If "none / base" selected (empty value), clear surcharge data
      if (!radioValue) {
        this._surchargeData[optionHandle] = {
          active: false,
          selectedVariantId: null,
          title: '',
          price: 0,
          priceFormatted: '',
          propertyName: '',
        };
        return;
      }

      // Surcharge card selected — read data attributes
      const card = label.closest('[data-surcharge-title]');
      if (!card) return;

      const title = card.dataset.surchargeTitle || '';
      const price = parseInt(card.dataset.surchargePrice || '0', 10);
      const variantId = card.dataset.surchargeVariantId || '';
      const priceEl = card.querySelector('[data-surcharge-price-display]');

      this._surchargeData[optionHandle] = {
        active: true,
        selectedVariantId: variantId,
        title,
        price,
        priceFormatted: priceEl ? priceEl.textContent.trim() : '',
        propertyName: card.dataset.surchargePropertyName || '',
      };
    }

    /**
     * Get the selected surcharge product for a given option handle.
     * Returns { variantId, title, price, priceFormatted } or null.
     */
    getSelectedSurchargeProduct(optionHandle) {
      const data = this._surchargeData[optionHandle];
      if (!data || !data.active) return null;
      return {
        variantId: data.selectedVariantId,
        title: data.title,
        price: data.price,
        priceFormatted: data.priceFormatted,
        propertyName: data.propertyName,
      };
    }

    /**
     * Get all selected surcharge products across all generic steps.
     * Returns an array of { optionHandle, variantId, title, price, priceFormatted }.
     */
    getAllSelectedSurchargeProducts() {
      const results = [];
      for (const [handle, data] of Object.entries(this._surchargeData)) {
        const selected = this.getSelectedSurchargeProduct(handle);
        if (selected) {
          results.push({ optionHandle: handle, ...selected });
        }
      }
      return results;
    }


    /**
     * Update the floating summary footer with current price and selected params.
     */
    updateFooter() {
      if (!this.footer || !this.productForm) return;

      const allOptions = this.productForm.querySelectorAll("product-option");
      const variantSelect = this.productForm.querySelector('select[name="id"]');

      if (!variantSelect) return;

      const currentVariantId = variantSelect.value;
      const currentVariant = this.variants.find(
        (v) => String(v.id) === String(currentVariantId),
      );

      // 1. Build selected parameters list
      if (this.footerParams) {
        this.footerParams.innerHTML = "";
        let paramIndex = 1;

        allOptions.forEach((optionEl) => {
          const handle = optionEl.dataset.handle || "";
          const name = handle.replace(/-/g, " ");
          let selectedValue = "";

          const checkedRadio = optionEl.querySelector(
            'input[type="radio"]:checked',
          );
          if (checkedRadio) {
            selectedValue = checkedRadio.value;
          } else if (optionEl.dataset.value) {
            selectedValue = optionEl.dataset.value;
          }

          if (selectedValue) {
            const param = document.createElement("span");
            param.className = "product-configurator__footer-param";
            param.textContent = `${paramIndex}. ${name}: ${selectedValue}`;
            param.title = `${name}: ${selectedValue}`;
            this.footerParams.appendChild(param);
            paramIndex++;
          }
        });

        // Append selected mattress as an extra line
        const mattress = this.getSelectedMattress();
        if (mattress) {
          const param = document.createElement("span");
          param.className = "product-configurator__footer-param product-configurator__footer-param--mattress";
          param.textContent = `${paramIndex}. ${mattress.title} ${mattress.priceFormatted}`;
          param.title = `${mattress.title} ${mattress.priceFormatted}`;
          this.footerParams.appendChild(param);
          paramIndex++;
        }

        // Append selected fabric family as an extra line
        const fabricFamily = this.getSelectedFabricFamily();
        if (fabricFamily) {
          const param = document.createElement("span");
          param.className = "product-configurator__footer-param product-configurator__footer-param--fabric-family";
          param.textContent = `${paramIndex}. ${fabricFamily.title} ${fabricFamily.priceFormatted}`;
          param.title = `${fabricFamily.title} ${fabricFamily.priceFormatted}`;
          this.footerParams.appendChild(param);
          paramIndex++;
        }

        // Append selected generic surcharge products as extra lines
        const surchargeProducts = this.getAllSelectedSurchargeProducts();
        surchargeProducts.forEach((sp) => {
          const param = document.createElement("span");
          param.className = `product-configurator__footer-param product-configurator__footer-param--${sp.optionHandle}`;
          const label = sp.propertyName
            ? `${sp.propertyName}: ${sp.title}`
            : `${sp.title} ${sp.priceFormatted}`.trim();
          param.textContent = `${paramIndex}. ${label}`;
          param.title = label;
          this.footerParams.appendChild(param);
          paramIndex++;
        });
      }

      // 2. Update footer price to include mattress surcharge
      if (this.footerPrice) {
        if (currentVariant) {
          let totalPrice = currentVariant.price;
          const mattress = this.getSelectedMattress();
          if (mattress) {
            totalPrice += mattress.price;
          }
          const fabricFamily = this.getSelectedFabricFamily();
          if (fabricFamily) {
            totalPrice += fabricFamily.price;
          }
          const surchargeProducts = this.getAllSelectedSurchargeProducts();
          surchargeProducts.forEach((sp) => {
            totalPrice += sp.price;
          });
          const formatted = (totalPrice / 100)
            .toFixed(2)
            .replace(".", ",")
            .replace(/,00$/, "");
          this.footerPrice.textContent = `${formatted} ${this.currencySymbol}`;
          this.footerPrice.classList.remove("loading");
        }
      }

      // 3. Update button state
      if (this.footerSubmit) {
        this.footerSubmit.disabled = currentVariant ? !currentVariant.available : true;
      }

      // Show the footer
      this.footer.style.display = "";
    }

    /**
     * Submit add-to-cart using the same logic as the product-form.
     */
    addToCart() {
      if (!this.productForm) return;

      const variantSelect = this.productForm.querySelector('select[name="id"]');
      const quantityInput = this.productForm.querySelector(
        'input[name="quantity"]',
      );

      if (!variantSelect || !variantSelect.value) return;

      // Gather properties
      const properties = {};
      this.productForm
        .querySelectorAll('input[name*="properties["]')
        .forEach((prop) => {
          if (prop.value) {
            const name = prop.name.replace("properties[", "").replace("]", "");
            properties[name] = prop.value;
          }
        });

      const items = [
        {
          id: variantSelect.value,
          quantity: quantityInput ? quantityInput.value : "1",
          properties: properties,
        },
      ];

      // Add selected mattress as a second cart item
      const mattress = this.getSelectedMattress();
      if (mattress) {
        items.push({
          id: mattress.variantId,
          quantity: quantityInput ? quantityInput.value : "1",
        });
      }

      // Add selected fabric family surcharge as a separate cart item
      const fabricFamily = this.getSelectedFabricFamily();
      if (fabricFamily) {
        items.push({
          id: fabricFamily.variantId,
          quantity: quantityInput ? quantityInput.value : "1",
        });
      }

      // Add selected generic surcharge products as separate cart items
      const surchargeProducts = this.getAllSelectedSurchargeProducts();
      surchargeProducts.forEach((sp) => {
        if (sp.variantId) {
          items.push({
            id: sp.variantId,
            quantity: quantityInput ? quantityInput.value : "1",
          });
        } else if (sp.propertyName && sp.title) {
          properties[sp.propertyName] = sp.title;
        }
      });

      const payload = { items };
      console.log('[Configurator] Add to cart payload:', JSON.stringify(payload, null, 2));

      this.footerSubmit.classList.add("loading");
      this.footerSubmit.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => {
          if (!res.ok) {
            return res.text().then((body) => {
              let message = 'Cart add failed (HTTP ' + res.status + ')';
              try {
                const errorData = JSON.parse(body);
                message = errorData.description || errorData.message || message;
              } catch (_) { /* response was HTML, not JSON */ }
              console.error("[Configurator] Cart add failed:", res.status, message);
              throw new Error(message);
            });
          }
          return res.json();
        })
        .then((data) => {
          if (data.items) {
            window.dispatchEvent(new CustomEvent("cartAdded"));
            window.dispatchEvent(new CustomEvent("cartCounterUpdate"));
          }
          setTimeout(() => {
            this.footerSubmit.classList.remove("loading");
            this.footerSubmit.disabled = false;
          }, 1000);
        })
        .catch((err) => {
          console.error("[Configurator] Add to cart error:", err);
          this.footerSubmit.classList.remove("loading");
          this.footerSubmit.disabled = false;
        });
    }

    /**
     * Reload the full product section by fetching the target URL.
     * Replaces the ENTIRE shopify-section content (so all custom elements
     * like product-form re-initialize properly), while preserving the
     * configurator drawer by detaching and re-inserting it.
     */
    reloadProductSection(targetUrl) {
      const section =
        this.closest(".shopify-section") ||
        document.querySelector(".section--pdp")?.closest(".shopify-section");

      if (!section) {
        this.isNavigating = false;
        window.location.href = targetUrl;
        return;
      }

      const sectionId = section.id.replace("shopify-section-", "");

      // Fetch the full page (no section_id) so we get the complete DOM
      // including all sections below the product
      fetch(targetUrl)
        .then((res) => {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const newSection = doc.getElementById(section.id);
          const newDrawerContent = doc.querySelector(
            "#drawer-product-configurator .drawer__scroll-inner",
          );
          const newFooterPrice = doc.querySelector("[data-footer-price]");
          const newConfiguratorEl = doc.querySelector("product-configurator");

          if (!newSection) {
            this.isNavigating = false;
            window.location.href = targetUrl;
            return;
          }

          // Save drawer scroll position BEFORE any DOM detach/replace
          const scrollContainer =
            this.content?.closest(".drawer__scroll") ||
            this.content?.closest(".drawer__body") ||
            this.content?.parentElement;
          const savedScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

          // Save mattress state BEFORE replacing drawer content
          let savedMattressTitle = null;
          let savedMattressIsNone = false;
          const activeCard = this.getActiveMattressCard();
          if (activeCard) {
            savedMattressTitle = activeCard.dataset.mattressTitle;
          } else {
            const noneRadio = this.content?.querySelector('#mattress-none');
            if (noneRadio && noneRadio.checked) savedMattressIsNone = true;
          }

          // 1. Detach the current configurator drawer so it survives section replacement
          const drawer = document.getElementById("drawer-product-configurator");
          const drawerPlaceholder = document.createComment("configurator-drawer-placeholder");
          if (drawer) {
            drawer.parentNode.insertBefore(drawerPlaceholder, drawer);
            drawer.remove();
          }

          // 2. Replace the ENTIRE section content with the new section
          //    This ensures product-form and all other custom elements
          //    properly re-initialize via connectedCallback
          section.innerHTML = newSection.innerHTML;

          // 3. Remove the new configurator drawer from the replaced section
          //    (we'll use our preserved one with the open drawer state)
          const newInsertedDrawer = section.querySelector("#drawer-product-configurator");
          let reInsertPoint = null;
          if (newInsertedDrawer) {
            reInsertPoint = newInsertedDrawer.nextSibling;
            newInsertedDrawer.remove();
          }

          // 4. Re-insert the preserved configurator drawer
          if (drawer) {
            if (reInsertPoint) {
              section.insertBefore(drawer, reInsertPoint);
            } else {
              section.appendChild(drawer);
            }
            drawerPlaceholder.remove();
          }

          // 5. Replace all other drawers (outside this section) that are product-specific
          const newDrawers = doc.querySelectorAll("drawer-section");
          newDrawers.forEach((newDrawer) => {
            if (newDrawer.id === "drawer-product-configurator") return;
            const currentDrawer = document.getElementById(newDrawer.id);
            if (currentDrawer) {
              currentDrawer.replaceWith(newDrawer.cloneNode(true));
            }
          });

          // Manually format the badge to "5.0/5" instead of relying on Judge.me JS
          // which prevents the "1 review" fallback while avoiding initializeBadges crashes.
          document.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
            const innerBadge = badge.querySelector('.jdgm-prev-badge');
            const textSpan = badge.querySelector('.jdgm-prev-badge__text');
            if (innerBadge && textSpan) {
              const numReviews = parseInt(innerBadge.getAttribute('data-number-of-reviews'), 10);
              if (numReviews > 0) {
                const ratingAttr = innerBadge.getAttribute('data-average-rating');
                const ratingVal = parseFloat(ratingAttr || "5").toFixed(1);
                textSpan.textContent = `${ratingVal}/5`;
              }
            }
            badge.classList.remove('jdgm-widget');
          });

          // Manually process timestamp spinners in the review drawer, because
          // Judge.me JS loses context when the DOM is replaced via AJAX.
          document.querySelectorAll('.jdgm-rev__timestamp.jdgm-spinner').forEach(span => {
            const content = span.getAttribute('data-content');
            if (content) {
              const datePart = content.split(' ')[0];
              const parts = datePart.split('-');
              if (parts.length === 3) {
                span.textContent = `${parts[2]}.${parts[1]}.${parts[0]}`;
              } else {
                span.textContent = datePart;
              }
            }
            span.classList.remove('jdgm-spinner');
          });

          // Re-initialize judge.me if it exists (for the review drawer pagination and form)
          if (window.jdgm && window.jdgm.initializeWidget) {
            setTimeout(() => {
              window.jdgm.initializeWidget();
            }, 200);
          }

          // 6. Update drawer options content
          if (newDrawerContent && this.content) {
            this.content.innerHTML = newDrawerContent.innerHTML;
          }

          // 7. Update component metadata for the new product
          if (newConfiguratorEl) {
            this.dataset.variants = newConfiguratorEl.dataset.variants;
            this.variants = JSON.parse(this.dataset.variants || "[]");
            this.dataset.currencySymbol =
              newConfiguratorEl.dataset.currencySymbol;
            this.currencySymbol = this.dataset.currencySymbol || "zł";
          }

          // 8. Update footer price
          if (newFooterPrice && this.footerPrice) {
            this.footerPrice.innerHTML = newFooterPrice.innerHTML;
            this.footerPrice.classList.add("loading");
          }

          // 9. Re-bind theme controls
          if (window.theme) {
            if (typeof window.theme.bindControls === "function") {
              window.theme.bindControls();
            }
            if (typeof window.theme.richtextParams === "function") {
              window.theme.richtextParams();
            }
          }

          // 10. Also replace other product-specific sections on the page
          //     (e.g. recommendations, related products)
          doc.querySelectorAll(".shopify-section").forEach((newSec) => {
            if (newSec.id === section.id) return; // already handled
            
            // Skip global sections like header and footer so their event listeners stay active
            if (
              newSec.closest("header-menu") ||
              newSec.closest("footer") ||
              newSec.id.includes("header") ||
              newSec.id.includes("footer") ||
              newSec.id.includes("announcement")
            ) {
              return;
            }

            const currentSec = document.getElementById(newSec.id);
            if (currentSec) {
              currentSec.innerHTML = newSec.innerHTML;
            }
          });

          // 11. Re-sync everything for the newly injected content
          setTimeout(() => {
            this.isNavigating = false;
            this.productForm = document.querySelector("product-form");
            this.syncActiveStates();
            if (this.productForm && typeof this.productForm.updateVariant === 'function') {
              this.productForm.updateVariant();
            }

            // Restore mattress check state BEFORE filterMattresses
            if (savedMattressTitle) {
              const cards = Array.from(this.content.querySelectorAll('.product-configurator__option--mattress-card'));
              const matchCard = cards.find(c => c.dataset.mattressTitle === savedMattressTitle);
              if (matchCard) {
                const step = matchCard.closest('.product-configurator__step');
                if (step) this.setActiveOption(step, matchCard);
              }
            } else if (savedMattressIsNone) {
              const noneRadio = this.content.querySelector('#mattress-none');
              if (noneRadio) {
                const noneOption = noneRadio.closest('.product-configurator__option');
                const step = noneOption?.closest('.product-configurator__step');
                if (noneOption && step) this.setActiveOption(step, noneOption);
              }
            }

            this.filterMattresses();
            this.updatePrices();
            this.updateFooter();
            if (this.loader) this.loader.style.display = "none";

            // Re-bind option click handlers for the new drawer content
            this.content
              .querySelectorAll(".product-configurator__option")
              .forEach((l) => {
                l.addEventListener("click", (e) => {
                  e.preventDefault();
                  this.onOptionClick(l);
                });
              });

            this.bindFabricDetailsButtons();

            // Restore drawer scroll position
            const scrollEl =
              this.content?.closest(".drawer__scroll") ||
              this.content?.parentElement;
            if (scrollEl && savedScrollTop) {
              scrollEl.scrollTop = savedScrollTop;
            }

            // Update PerfectScrollbar instances on the drawer
            const drawerEl = this.closest("drawer-section");
            if (drawerEl && drawerEl.scrollbars) {
              drawerEl.scrollbars.forEach((s) => s.update());
            }
          }, 150);
        })
        .catch((err) => {
          this.isNavigating = false;
          console.error("Error reloading product section:", err);
          window.location.href = targetUrl;
        });
    }

    /**
     * Re-bind click event listeners to fabric details buttons.
     */
    bindFabricDetailsButtons(container = document) {
      const fabricDetailsEl = document.querySelector("fabric-details");
      if (!fabricDetailsEl) return;

      const buttons = container.querySelectorAll(".fab-details-btn");
      buttons.forEach((btn) => {
        if (btn.dataset.bound) return;

        btn.addEventListener("click", (e) => {
          e.preventDefault();
          const handle = btn.dataset.handle;
          const drawerId = btn.getAttribute("aria-controls");

          if (handle && typeof fabricDetailsEl.fetchDetails === "function") {
            fabricDetailsEl.fetchDetails(handle);
          }

          if (drawerId) {
            window.dispatchEvent(
              new CustomEvent("drawerOpen", {
                detail: {
                  id: drawerId,
                },
              }),
            );
          }
        });

        btn.dataset.bound = "true";
      });
    }

    /**
     * Filter mattress cards based on the currently selected "Powierzchnia spania" value.
     * Shows only mattress cards that have a variant matching the selected size,
     * and updates the displayed price and radio value to that variant.
     */
    filterMattresses() {
      if (!this.content) return;

      const sizeHandle = this.dataset.bedSizeHandle;
      if (!sizeHandle) return;

      const sizeOption = this.productForm?.querySelector(
        `product-option[data-handle="${CSS.escape(sizeHandle)}"]`,
      );
      if (!sizeOption) return;

      const checkedRadio = sizeOption.querySelector(
        'input[type="radio"]:checked',
      );
      const selectedSize = checkedRadio
        ? checkedRadio.value
        : sizeOption.dataset.value || "";

      if (!selectedSize) return;

      const currencySymbol = this.currencySymbol;

      this.content
        .querySelectorAll(".product-configurator__option--mattress-card")
        .forEach((card) => {
          const variantsRaw = card.dataset.mattressVariants;
          if (!variantsRaw) return;

          let variants;
          try {
            variants = JSON.parse(variantsRaw);
          } catch (e) {
            return;
          }

          const match = variants.find((v) => v.size === selectedSize);

          if (match) {
            card.style.display = "";

            // Update radio value to the matched variant ID
            const radio = card.querySelector('input[type="radio"]');
            if (radio) {
              radio.value = match.id;
              radio.id = `mattress-${match.id}`;
            }
            const label = card.querySelector(".product-configurator__option-label");
            if (label) {
              label.setAttribute("for", `mattress-${match.id}`);
            }

            // Update displayed price
            const priceEl = card.querySelector("[data-mattress-price]");
            if (priceEl) {
              const priceFormatted = (match.price / 100)
                .toFixed(2)
                .replace(".", ",")
                .replace(/,00$/, "");
              priceEl.textContent = `+${priceFormatted} ${currencySymbol}`;
            }
          } else {
            const radio = card.querySelector('input[type="radio"]');
            if (radio?.checked) {
              radio.checked = false;

              const noneRadio = this.content.querySelector('#mattress-none');
              if (noneRadio) {
                noneRadio.checked = true;
                const noneOption = noneRadio.closest(
                  ".product-configurator__option",
                );
                const mattressStep = noneRadio.closest(
                  ".product-configurator__step",
                );
                if (mattressStep && noneOption) {
                  this.setActiveOption(mattressStep, noneOption);
                }
              }

              card.classList.remove("product-configurator__option--active");
            }

            card.style.display = "none";
          }

        });
    }
  }

  customElements.define("product-configurator", ProductConfigurator);
}
